import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, User, LogIn } from 'lucide-react';
import { categories } from '../mockData';
import { useAuth } from '../contexts/AuthContext';

const Header = () => {
  const { user, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
    }
  };

  // Get current date formatted
  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      {/* Top Bar */}
      <div className="border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center text-sm">
          <div className="text-gray-600 text-xs sm:text-sm">{getCurrentDate()}</div>
          
          {/* Desktop Links - Hidden on mobile */}
          <div className="hidden lg:flex items-center gap-6">
            <Link to="/share-good-news" className="text-green-600 hover:text-green-700 transition-colors font-semibold">Share Good News</Link>
            <Link to="/media-alerts" className="text-gray-600 hover:text-gray-900 transition-colors">Media Alerts</Link>
            <Link to="/about" className="text-gray-600 hover:text-gray-900 transition-colors">About</Link>
            <Link to="/contact" className="text-gray-600 hover:text-gray-900 transition-colors">Contact</Link>
            <Link to="/newsletter" className="text-gray-600 hover:text-gray-900 transition-colors">Newsletter</Link>
            {isAuthenticated ? (
              <>
                <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                  <User size={16} />
                  <span className="font-medium">{user?.name}</span>
                </Link>
                <Link to="/admin" className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors text-sm font-medium">
                  Admin
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
                  <LogIn size={16} />
                  Login
                </Link>
                <Link to="/signup" className="px-4 py-1 bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors text-sm">
                  Sign Up
                </Link>
              </>
            )}
          </div>
          
          {/* Mobile Login/Signup - Always visible on top right */}
          <div className="lg:hidden flex items-center gap-2">
            {isAuthenticated ? (
              <Link to="/dashboard" className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-xs">
                <User size={16} />
                <span className="hidden sm:inline">{user?.name}</span>
              </Link>
            ) : (
              <>
                <Link to="/login" className="flex items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors text-xs">
                  <LogIn size={16} />
                  <span className="hidden sm:inline">Login</span>
                </Link>
                <Link to="/signup" className="px-2 sm:px-3 py-1 bg-gray-900 text-white rounded hover:bg-gray-800 transition-colors text-xs">
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Logo */}
          <Link to="/" className="flex-1 lg:flex-none">
            <h1 className="text-center lg:text-left">
              <span className="block text-4xl lg:text-6xl font-newspaper tracking-tight" style={{ 
                letterSpacing: '-0.01em', 
                fontWeight: 500,
                wordSpacing: '-0.1em'
              }}>
                The Central Coast Times
              </span>
              <span className="block text-xs lg:text-sm text-gray-600 mt-1 font-sans tracking-wide">
                INNOVATION, PROGRESS & FREE MARKETS
              </span>
            </h1>
          </Link>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden lg:flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search articles..."
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent w-64"
            />
            <button
              type="submit"
              className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Search size={20} />
            </button>
          </form>
        </div>
      </div>

      {/* Navigation */}
      <nav className={`${
        isMenuOpen ? 'block' : 'hidden'
      } lg:block border-t border-gray-200 bg-white`}>
        <div className="max-w-7xl mx-auto px-4">
          {/* Categories */}
          <ul className="flex flex-col lg:flex-row lg:justify-center lg:space-x-8 py-3">
            {categories.map((category) => (
              <li key={category.id}>
                <Link
                  to={`/category/${category.slug}`}
                  className="block py-2 lg:py-0 text-gray-700 hover:text-gray-900 font-medium transition-colors uppercase text-sm tracking-wide"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {category.name}
                </Link>
              </li>
            ))}
          </ul>
          
          {/* Mobile-Only Links */}
          <div className="lg:hidden border-t border-gray-200 py-3 space-y-2">
            {isAuthenticated && (
              <Link 
                to="/admin" 
                className="block py-2 px-3 bg-blue-600 text-white font-semibold rounded-lg"
                onClick={() => setIsMenuOpen(false)}
              >
                🔧 Admin Dashboard
              </Link>
            )}
            <Link 
              to="/about" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className="block py-2 text-gray-700 hover:text-gray-900"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Search */}
      <div className="lg:hidden border-t border-gray-200 px-4 py-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search articles..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
          <button
            type="submit"
            className="p-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <Search size={20} />
          </button>
        </form>
      </div>
    </header>
  );
};

export default Header;